import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { TextField, MenuItem, Box, Grid, Typography } from '@mui/material';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import { debounce } from 'lodash';
import {
  addCompany,
  addCompanyGst,
  clearCompanyDetails,
  fetchParentCompanyDetails,
  fetchParentCompanys,
  updateCompany,
  updateCompanyGst,
} from 'redux/actions/companyAction';
import { getDutyType } from 'redux/actions/UserDutyType';
import { getCar } from 'redux/actions/UserCar';
import { getServiceCity } from 'redux/actions/UserServiceCity';
import CommonMultiForm from 'components/commonMultiForm/CommonMultiForm';
import BackDrop from 'components/backDrop/BackDrop';
import CommonButton from 'components/commonButton/CommonButton';
import { getDateFormat, optionsList } from 'utils/utils';
import { GSTFormData, formData } from './CompanyData';
import { BlueSwitchBtn } from 'pages/configuration/addCompliance/AddComplianceStyleTab';
import taxService from 'services/TaxService';

const CompanyOnboarding = (props) => {
  const [contractDetails, setContractDetails] = useState(true);
  const [skipClicked, setSkipClicked] = useState(false);
  const [adminMembers, setAdminMembers] = useState(1);
  const [financeMembers, setFinanceMembers] = useState(1);
  const [taxMasterList, setTaxMasterList] = useState([]);
  const [noShowConfig, setNoShowConfig] = useState(null);
  useEffect(() => {
    const fetchTaxes = async () => {
      try {
        const res = await taxService.getTaxList();
        if (res?.data) {
          setTaxMasterList(res.data);
        }
      } catch (err) {
        console.error('Error fetching tax master list:', err);
      }
    };
    fetchTaxes();
  }, []);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  let values = state?.companyProfile;
  const cityList = useSelector((state) => state?.userServiceCity?.userServiceCity?.data);
  const onboardedCompanyData = useSelector((state) => state?.addCompany?.onBoardedCompay?.data);

  const updateOnboardedCompanyDatas = useSelector(
    (state) => state?.updateCompany?.onBoardedCompay?.data,
  );

  const parentCompanyList = useSelector(
    (state) => state?.parentCompanyList?.parentCompanyList?.body,
  );
  const parentCompanyListLoader = useSelector((state) => state?.parentCompanyList?.loading);

  const cityListLoader = useSelector((state) => state?.userServiceCity?.userServiceCity?.success);

  const parentCompanyAdminDetails = useSelector(
    (state) => state?.userParentCompanyDetails?.parentCompanyDetails,
  );

  useEffect(() => {
    dispatch(getCar());
    dispatch(getDutyType());
    dispatch(getServiceCity());
    dispatch(fetchParentCompanys());
    dispatch(clearCompanyDetails());

    return () => {
      dispatch(clearCompanyDetails());
    };
  }, []);
  useEffect(() => {
    if (values?.additionalInfo?.noShowConfig) {
      setNoShowConfig(values.additionalInfo.noShowConfig);
    }
  }, [values?.additionalInfo?.noShowConfig]);
  const parentID =
    onboardedCompanyData?.parentCompanyId ||
    updateOnboardedCompanyDatas?.parentCompanyId ||
    state?.companyProfile?.parentCompanyId;
  const switchDisable =
    onboardedCompanyData?.hasParent ||
    updateOnboardedCompanyDatas?.hasParent ||
    state?.companyProfile?.hasParent ||
    false;
  const hasParentAdmin =
    onboardedCompanyData?.isParentAdmin ||
    updateOnboardedCompanyDatas?.isParentAdmin ||
    state?.companyProfile?.isParentAdmin;
  const hasParentFinance =
    onboardedCompanyData?.isParentFinance ||
    updateOnboardedCompanyDatas?.isParentFinance ||
    state?.companyProfile?.isParentFinance;

  // Sync member counts with existing edit data
  useEffect(() => {
    if (state?.edit && state?.companyProfile?.Employees) {
      const adminsCount = state.companyProfile.Employees.filter(
        (e) => e.userRole === 'Admin',
      ).length;
      const financesCount = state.companyProfile.Employees.filter(
        (e) => e.userRole === 'Finance',
      ).length;
      if (adminsCount > 0) setAdminMembers(adminsCount);
      if (financesCount > 0) setFinanceMembers(financesCount);
    }
  }, []);

  const parentData = {
    id: parentID,
  };

  useEffect(() => {
    if (parentData?.id) {
      dispatch(fetchParentCompanyDetails({ id: parentID }));
    }
  }, [parentID]);

  const cityOption = optionsList(cityList, 'cityOption');
  const parentCompanyListOption = optionsList(parentCompanyList, 'parentCompanyList');

  const vehicleData = state?.companyProfile?.Contracts?.[0]?.ContractDetails?.filter(
    (obj) => obj.type === 'vehicle',
  );
  const dutyData = state?.companyProfile?.Contracts?.[0]?.ContractDetails?.filter(
    (obj) => obj.type === 'dutyType',
  );
  const cityData = state?.companyProfile?.Contracts?.[0]?.ContractDetails?.filter(
    (obj) => obj.type === 'city',
  );

  const adminData = state?.companyProfile?.Employees?.filter((obj) => obj.userRole === 'Admin');

  const financeData = state?.companyProfile?.Employees?.filter(
    (obj) => obj?.userRole === 'Finance',
  );

  const branchId = state?.companyProfile?.branch;

  // Build indexed default values for all admin/finance members
  const adminGstDefaults = {};
  (adminData || []).forEach((a, i) => {
    adminGstDefaults[`adminName_${i}`] = a?.name;
    adminGstDefaults[`adminEmail_${i}`] = a?.email;
    adminGstDefaults[`adminPhone_${i}`] = a?.phoneNumber;
    adminGstDefaults[`adminId_${i}`] = a?.id;
  });
  const financeGstDefaults = {};
  (financeData || []).forEach((f, i) => {
    financeGstDefaults[`financeName_${i}`] = f?.name;
    financeGstDefaults[`financeEmail_${i}`] = f?.email;
    financeGstDefaults[`financePhone_${i}`] = f?.phoneNumber;
    financeGstDefaults[`financeId_${i}`] = f?.id;
  });

  const gstDetails = {
    ...adminGstDefaults,
    ...financeGstDefaults,
    vehicleId: vehicleData?.[0]?.vehicle?.id,
    dutyId: dutyData?.[0]?.dutyType?.dutyTypeId,
    dutyTypeName: dutyData?.[0]?.dutyType?.name,
    cityId: cityData?.[0]?.city?.id,
  };

  const onScrollTop = () => {
    const element = document.getElementsByClassName('formDiv');
    element[0].scroll({
      top: 0,
      behavior: 'smooth',
    });
  };

  //onsubmit contract details
  const onSubmitContractDetails = (
    data,
    image,
    logo,
    country,
    cities,
    e,
    selectedMultiFiles,
    deleteFileId,
    dynamicDomain,
  ) => {
    let formData = new FormData();
    for (let i = 0; i < cities?.length; i++) {
      formData.append(`contract[cities][${i}][id]`, cities[i][`city-${i}`]);
      formData.append(`contract[cities][${i}][address]`, cities[i][`address-${i}`]);
      formData.append(`contract[cities][${i}][gstNumber]`, cities[i][`gstNumber-${i}`]);
      formData.append(
        `contract[cities][${i}][sez]`,
        cities[i][`sez-${i}`] ? cities[i][`sez-${i}`] : false,
      );
      formData.append(`contract[cities][${i}][state]`, cities[i][`state-${i}`]);
      formData.append(`contract[cities][${i}][isHeadquarter]`, i === 0 ? true : false);
      formData.append(`contract[cities][${i}][garageAddress]`, cities[i][`garageAddress-${i}`]);
      formData.append(`contract[cities][${i}][garageLocation]`, [cities[i][`garageLocation-${i}`]]);
    }

    for (let i = 0; i < data?.chooseDuties?.length; i++) {
      formData.append(`contract[dutyType][${i}]`, data?.chooseDuties[i]?.value);
    }

    for (let i = 0; i < dynamicDomain?.length; i++) {
      formData.append(`allowedDomain[${i}]`, dynamicDomain[i][`allowedDomain-${i}`]);
    }

    const companyData = {
      name: data?.name,
      address: data?.address,
      parentCompanyId: data?.parentCompanyId?.value
        ? data?.parentCompanyId?.value
        : data?.parentCompanyId
          ? data?.parentCompanyId
          : 'null',
      cityId: data?.cityId,
      pinCode: data?.pinCode,
      panNumber: data?.panNumber,
      email: data?.email,
      phoneNumber: data?.phoneNumber,
      companyId: '',
      billingType: data?.billToSelf && data?.billToCompany ? '3' : data?.billToSelf ? '1' : '2',
      countryCode: country,
      billingCycle: data?.billingCycle,
      invoiceType: data?.invoiceType,
      config: data?.config,
      approvalDuration: data?.approvalDuration,
      startOtpRequired: data?.startOtpRequired ? true : false,
      endOtpRequired: data?.endOtpRequired ? true : false,
      tripOtpOverrideEnabled: data?.tripOtpOverrideEnabled ? true : false,
      paymentTriggerMode: data?.billToSelf
        ? data?.paymentTriggerMode || 'afterInvoice'
        : 'afterInvoice',

      selfRegistrationAllowed: data?.selfRegistrationAllowed || false,

      approvalRequired: data?.selfRegistrationAllowed ? data?.approvalRequired : null,

      additionalInfo: JSON.stringify({
        billingLabelOverrides: {
          billToSelf: data?.billToSelfLabel || 'Bill to Self',
          billToCompany: data?.billToCompanyLabel || 'Bill to Company',
        },
        paymentTriggerMode: data?.billToSelf
          ? data?.paymentTriggerMode || 'afterInvoice'
          : 'afterInvoice',
        nightAllowanceWindow: {
          start: data?.nightAllowanceStart || '',
          end: data?.nightAllowanceEnd || '',
        },
        earlyStartAllowanceWindow: {
          start: data?.earlyStartAllowanceStart || '',
          end: data?.earlyStartAllowanceEnd || '',
        },
        guestSignatureRequired:
          data?.guestSignatureRequired === undefined || data?.guestSignatureRequired === null
            ? true
            : data?.guestSignatureRequired,

        costCenterConfig: {
          visible: data?.costCentreVisible ?? false,
          mandatory: data?.costCentreVisible ? (data?.costCentreMandatory ?? false) : false,
          label: data?.costCentreVisible
            ? data?.costCentreLabel?.trim() || 'Cost Center'
            : 'Cost Center',
        },
      }),

      defaultIgstTaxId: data?.defaultIgstTaxId || '',
      defaultCgstTaxId: data?.defaultCgstTaxId || '',
      defaultSgstTaxId: data?.defaultSgstTaxId || '',
      ...(data.garageRateType && {
        garageRateType: data.garageRateType,
        garageRateConfig: JSON.stringify(data.garageRateConfig),
      }),
    };
    formData.append('agreement', image);
    if (logo) {
      formData.append('logo', logo);
    }
    selectedMultiFiles?.forEach((file, index) => {
      formData.append(`documents`, file);
    });
    formData.append(
      'contract[date]',
      getDateFormat(data?.chooseDate['$d'], 'onboardingDateFormat'),
    );
    formData.append('contract[startDate]', getDateFormat(data?.tenure[0], 'tenureDateFormat'));
    formData.append('contract[endDate]', getDateFormat(data?.tenure[1], 'tenureDateFormat'));

    if (state?.edit === true) {
      formData.append('id', state?.companyProfile.id);
      for (let i = 0; i < branchId?.length; i++) {
        formData.append(`contract[cities][${i}][branchId]`, branchId[i].id);
      }

      for (let i = 0; i < deleteFileId?.length; i++) {
        formData.append(`removeIds[${i}]`, deleteFileId[i]);
      }
    }

    //Passing zero if config rate is not Point-To-Garage
    formData.append(`configRate`, data?.config === 'Point-To-Garage' ? data?.configRate : 0);

    Object.keys(companyData).map((val) => {
      formData.append(val, companyData[val]);
    });
    // NEW: Append No-Show Config as flat Form-Data keys
    const noShowTypes = ['Rental', 'Airport', 'Outstation'];
    noShowTypes.forEach((type) => {
      const modeValue = data?.[`noShow${type}Mode`];
      const amountValue = data?.[`noShow${type}FixedAmount`];

      formData.append(`noShow${type}Mode`, modeValue || 'tripFare');

      if (modeValue === 'fixedAmount') {
        formData.append(`noShow${type}FixedAmount`, amountValue || '');
      } else {
        formData.append(`noShow${type}FixedAmount`, '');
      }
    });

    // Seal & Signature config (company override / global fallback)
    formData.append('sealSignatureEnabled', data?.sealSignatureEnabled ?? false);
    formData.append('sealSignatureOverride', data?.sealSignatureOverride ?? false);
    if (data?.sealSignatureSeal) {
      formData.append('sealSignatureSeal', data.sealSignatureSeal);
    }
    if (data?.sealSignatureSignature) {
      formData.append('sealSignatureSignature', data.sealSignatureSignature);
    }
    formData.append('removeSealSignatureSeal', data?.removeSealSignatureSeal ?? false);
    formData.append('removeSealSignatureSignature', data?.removeSealSignatureSignature ?? false);

    const smsTriggerScenarios = {
      smsNotificationsEnabled:
        data?.smsNotificationsEnabled === undefined ? true : data?.smsNotificationsEnabled,
      bookingConfirmation:
        data?.bookingConfirmation === undefined ? true : data?.bookingConfirmation,
      vehicleAssignment: data?.vehicleAssignment === undefined ? true : data?.vehicleAssignment,
      vehicleReassignment:
        data?.vehicleReassignment === undefined ? true : data?.vehicleReassignment,
      tripStart: data?.tripStart === undefined ? true : data?.tripStart,
      tripCompletion: data?.tripCompletion === undefined ? true : data?.tripCompletion,
      tripCancellation: data?.tripCancellation === undefined ? true : data?.tripCancellation,
      tripReschedule: data?.tripReschedule === undefined ? true : data?.tripReschedule,
      paymentLink: data?.paymentLink === undefined ? true : data?.paymentLink,
      otp: data?.otp === undefined ? true : data?.otp,
    };

    formData.append('smsTriggerScenarios', JSON.stringify(smsTriggerScenarios));

    if (state?.edit === true) {
      dispatch(
        updateCompany(formData, {
          pageType: 'companyOnboardingPage',
          setContractDetails: setContractDetails,
        }),
      );
    } else {
      dispatch(addCompany(formData, setContractDetails));
    }
    onScrollTop();
  };

  //onsubmit gst billing details
  const onSubmitaCompanyGst = (
    data,
    image,
    logo,
    country,
    commonSwitch,
    _e,
    _files,
    _delIds,
    _domains,
    _switch,
    dynamicAdmins,
    dynamicFinances,
  ) => {
    // Build admin array from the dynamic member rows
    const admin = (dynamicAdmins || []).map((a) => ({
      ...(a?.id ? { id: a.id } : {}),
      name: a?.name || '',
      countryCode: a?.countryCode || country || '+91',
      mobileNumber: a?.phone || '',
      email: a?.email || '',
    }));

    // Build finance array from the dynamic member rows
    const finance = (dynamicFinances || []).map((f) => ({
      ...(f?.id ? { id: f.id } : {}),
      name: f?.name || '',
      countryCode: f?.countryCode || country || '+91',
      mobileNumber: f?.phone || '',
      email: f?.email || '',
    }));

    if (state?.edit) {
      const gstData = {
        id: state?.companyProfile.id,
        isParentAdmin: commonSwitch?.admin,
        isParentFinance: commonSwitch?.finance,
        admin,
        finance,
      };
      dispatch(updateCompanyGst(gstData, navigate));
    } else {
      const gstData = {
        companyId: onboardedCompanyData?.id,
        isParentAdmin: commonSwitch?.admin || false,
        isParentFinance: commonSwitch?.finance || false,
        admin,
        finance,
      };
      dispatch(addCompanyGst(gstData, navigate));
    }
    onScrollTop();
  };

  const handleNavigate = () => {
    if (contractDetails) {
      navigate('/companies', { replace: true });
    } else {
      setContractDetails(true);
      navigate('/companies/new-booking', {
        state: {
          edit: true,
          companyProfile: state?.edit
            ? skipClicked
              ? onboardingDefaultValues
              : updateOnboardedCompanyDatas
            : onboardedCompanyData,
        },
      });
    }
  };

  // const companyProfile = {
  //   ...state?.companyProfile,
  //   parentCompanyId: state?.companyProfile?.parentCompanyId && {
  //     value: state?.companyProfile?.parentCompanyId,
  //     label: state?.companyProfile?.parentCompanyName,
  //   },
  //   tenure: [values?.Contracts?.[0].startDate, values?.Contracts?.[0].endDate],
  //   billingDetails: state?.companyProfile?.Contracts?.[0]?.billingType && true,
  //   billingType: state?.companyProfile?.Contracts?.[0]?.billingType,
  //   billToCreditCard:
  //     (state?.companyProfile?.Contracts?.[0]?.billingType === 1 ||
  //       state?.companyProfile?.Contracts?.[0]?.billingType === 3) &&
  //     "billToCreditCard",
  //   billToCompany:
  //     (state?.companyProfile?.Contracts?.[0]?.billingType === 2 ||
  //       state?.companyProfile?.Contracts?.[0]?.billingType === 3) &&
  //     "billToCompany",
  //   approvalDuration: state?.companyProfile?.Contracts?.[0]?.approvalDuration,
  //   billingCycle: state?.companyProfile?.Contracts?.[0]?.billingCycle,
  //   invoiceType: state?.companyProfile?.Contracts?.[0]?.invoiceType,
  //   config: state?.companyProfile?.Contracts?.[0]?.config,
  //   configRate: state?.companyProfile?.Contracts?.[0]?.configRate,
  //   endOtpRequired: state?.companyProfile?.endOtpRequired || false,
  //   ...(values?.additionalInfo ? values?.additionalInfo : {}),
  // };

  const companyProfile = {
    ...state?.companyProfile,
    parentCompanyId: state?.companyProfile?.parentCompanyId && {
      value: state?.companyProfile?.parentCompanyId,
      label: state?.companyProfile?.parentCompanyName,
    },
    tenure: [values?.Contracts?.[0].startDate, values?.Contracts?.[0].endDate],
    billingDetails: state?.companyProfile?.Contracts?.[0]?.billingType && true,
    billingType: state?.companyProfile?.Contracts?.[0]?.billingType,

    // billToCreditCard:
    //   (state?.companyProfile?.Contracts?.[0]?.billingType === 1 ||
    //     state?.companyProfile?.Contracts?.[0]?.billingType === 3) &&
    //   "billToCreditCard",

    // billToCompany:
    //   (state?.companyProfile?.Contracts?.[0]?.billingType === 2 ||
    //     state?.companyProfile?.Contracts?.[0]?.billingType === 3) &&
    //   "billToCompany",

    billToSelf:
      (state?.companyProfile?.Contracts?.[0]?.billingType === 1 ||
        state?.companyProfile?.Contracts?.[0]?.billingType === 3) &&
      'billToSelf',

    billToCompany:
      (state?.companyProfile?.Contracts?.[0]?.billingType === 2 ||
        state?.companyProfile?.Contracts?.[0]?.billingType === 3) &&
      'billToCompany',

    approvalDuration: state?.companyProfile?.Contracts?.[0]?.approvalDuration,
    billingCycle: state?.companyProfile?.Contracts?.[0]?.billingCycle,
    invoiceType: state?.companyProfile?.Contracts?.[0]?.invoiceType,
    config: state?.companyProfile?.Contracts?.[0]?.config,
    configRate: state?.companyProfile?.Contracts?.[0]?.configRate,
    startOtpRequired:
      state?.companyProfile?.startOtpRequired ?? values?.additionalInfo?.startOtpRequired ?? false,
    endOtpRequired:
      state?.companyProfile?.endOtpRequired ?? values?.additionalInfo?.endOtpRequired ?? false,
    tripOtpOverrideEnabled:
      state?.companyProfile?.tripOtpOverrideEnabled ??
      values?.additionalInfo?.tripOtpOverrideEnabled ??
      false,
    paymentTriggerMode: values?.additionalInfo?.paymentTriggerMode || 'afterInvoice',

    guestSignatureRequired:
      values?.additionalInfo?.guestSignatureRequired === undefined ||
      values?.additionalInfo?.guestSignatureRequired === null
        ? true
        : values?.additionalInfo?.guestSignatureRequired,

    costCentreVisible: values?.additionalInfo?.costCenterConfig?.visible ?? false,
    costCentreMandatory: values?.additionalInfo?.costCenterConfig?.mandatory ?? false,
    costCentreLabel: values?.additionalInfo?.costCenterConfig?.label || '',

    smsNotificationsEnabled:
      values?.additionalInfo?.smsTriggerScenarios?.smsNotificationsEnabled === undefined
        ? false
        : values?.additionalInfo?.smsTriggerScenarios?.smsNotificationsEnabled,
    bookingConfirmation:
      values?.additionalInfo?.smsTriggerScenarios?.bookingConfirmation === undefined
        ? true
        : values?.additionalInfo?.smsTriggerScenarios?.bookingConfirmation,
    vehicleAssignment:
      values?.additionalInfo?.smsTriggerScenarios?.vehicleAssignment === undefined
        ? true
        : values?.additionalInfo?.smsTriggerScenarios?.vehicleAssignment,
    vehicleReassignment:
      values?.additionalInfo?.smsTriggerScenarios?.vehicleReassignment === undefined
        ? true
        : values?.additionalInfo?.smsTriggerScenarios?.vehicleReassignment,
    tripStart:
      values?.additionalInfo?.smsTriggerScenarios?.tripStart === undefined
        ? true
        : values?.additionalInfo?.smsTriggerScenarios?.tripStart,
    tripCompletion:
      values?.additionalInfo?.smsTriggerScenarios?.tripCompletion === undefined
        ? true
        : values?.additionalInfo?.smsTriggerScenarios?.tripCompletion,
    tripCancellation:
      values?.additionalInfo?.smsTriggerScenarios?.tripCancellation === undefined
        ? true
        : values?.additionalInfo?.smsTriggerScenarios?.tripCancellation,
    tripReschedule:
      values?.additionalInfo?.smsTriggerScenarios?.tripReschedule === undefined
        ? true
        : values?.additionalInfo?.smsTriggerScenarios?.tripReschedule,
    paymentLink:
      values?.additionalInfo?.smsTriggerScenarios?.paymentLink === undefined
        ? true
        : values?.additionalInfo?.smsTriggerScenarios?.paymentLink,
    otp:
      values?.additionalInfo?.smsTriggerScenarios?.otp === undefined
        ? true
        : values?.additionalInfo?.smsTriggerScenarios?.otp,

    ...(values?.additionalInfo ? values?.additionalInfo : {}),

    selfRegistrationAllowed: values?.additionalInfo?.selfRegistrationAllowed || false,

    approvalRequired: values?.additionalInfo?.approvalRequired ?? '',

    // Billing Label Config
    billToSelfLabel: values?.additionalInfo?.billingLabelOverrides?.billToSelf || '',

    billToCompanyLabel: values?.additionalInfo?.billingLabelOverrides?.billToCompany || '',
    defaultIgstTaxId: state?.companyProfile?.defaultIgstTaxId || '',
    defaultCgstTaxId: state?.companyProfile?.defaultCgstTaxId || '',
    defaultSgstTaxId: state?.companyProfile?.defaultSgstTaxId || '',
    // No-Show Configuration — pre-populate from additionalInfo.noShowConfig
    noShowRentalMode: values?.additionalInfo?.noShowConfig?.rental?.mode || 'tripFare',
    noShowRentalFixedAmount: values?.additionalInfo?.noShowConfig?.rental?.fixedAmount || '',
    noShowAirportMode: values?.additionalInfo?.noShowConfig?.airport?.mode || 'tripFare',
    noShowAirportFixedAmount: values?.additionalInfo?.noShowConfig?.airport?.fixedAmount || '',
    noShowOutstationMode: values?.additionalInfo?.noShowConfig?.outstation?.mode || 'tripFare',
    noShowOutstationFixedAmount:
      values?.additionalInfo?.noShowConfig?.outstation?.fixedAmount || '',
    ...(values?.additionalInfo ? values?.additionalInfo : {}),

    // Seal & Signature config
    sealSignatureEnabled: values?.additionalInfo?.sealSignature?.enabled ?? false,
    sealSignatureOverride: values?.additionalInfo?.sealSignature?.override ?? false,

    // Expose the full sealSignature object so the CMF upload control can
    // read existing URLs + isDefault flag to render the current preview.
    sealSignature: values?.additionalInfo?.sealSignature || null,
  };

  const onboardingDefaultValues = state?.edit ? companyProfile : '';

  const btnAction = () => {
    setContractDetails(false);
    setSkipClicked(true);
  };

  const onHandleAutoSuggestion = debounce((event, value, fieldName) => {
    if (fieldName === 'parentCompanyName') {
      dispatch(
        fetchParentCompanys(
          { search: value },
          { searchType: 'globalSearch', loader: 'backDropLoader' },
        ),
      );
    }
  }, 500);

  const onboardingTitle = state?.edit ? 'Edit Company' : 'Add new company';
  const companyTitles = ['Edit Company', 'Add new company', 'Admin Team Details'];

  const activeTaxes = taxMasterList.filter((tax) => tax.isActive);
  const taxOptions = activeTaxes.map((tax) => ({
    label: `${tax.name} - ${tax.percentage}%`,
    value: tax.id,
  }));

  return (
    <Grid container className="pt20 br20" justifyContent={'center'}>
      <Grid item xs={11} lg={11} className="p30 mb20  bgLightWhite">
        {cityListLoader && !parentCompanyListLoader ? (
          <Box>
            {contractDetails && (
              <CommonMultiForm
                formData={formData(
                  parentCompanyListOption,
                  cityOption,
                  values,
                  onboardingTitle,
                  state?.edit,
                  taxOptions,
                )}
                companyEdit={state?.edit && true}
                formType={'companyOnbaording'}
                onSubmittBtn={onSubmitContractDetails}
                handleNavigate={handleNavigate}
                companyTitles={companyTitles}
                button={'Save & Continue'}
                cityList={cityList}
                defaultValues={onboardingDefaultValues}
                onHandleAutoSuggestion={onHandleAutoSuggestion}
                btnAction={btnAction}
                taxMasterList={taxMasterList}
              />
            )}

            {!contractDetails && (
              <>
                <CommonMultiForm
                  formData={GSTFormData(adminMembers, financeMembers)}
                  onSubmittBtn={onSubmitaCompanyGst}
                  button={adminData?.[0]?.name ? 'Update' : 'Submit'}
                  contractId={
                    onboardedCompanyData?.Contracts?.[0]?.Agreements?.[0]?.contractId ||
                    onboardingDefaultValues?.Contracts?.[0]?.CompanyContracts?.ContractId ||
                    onboardedCompanyData?.agreement?.contractId ||
                    updateOnboardedCompanyDatas?.Contracts?.[0]?.CompanyContracts?.ContractId
                  }
                  defaultValues={gstDetails}
                  rateCard={true}
                  companyEdit={state?.edit && true}
                  companyPage={'companyGst'}
                  companyEmail={
                    state?.edit
                      ? skipClicked
                        ? onboardingDefaultValues
                        : updateOnboardedCompanyDatas
                      : onboardedCompanyData
                  }
                  handleNavigate={handleNavigate}
                  companyTitles={companyTitles}
                  formName="companyAdminFinance"
                  hasParentFinance={hasParentFinance}
                  hasParentAdmin={hasParentAdmin}
                  switchDisable={switchDisable}
                  parentCompanyAdminDetails={parentCompanyAdminDetails}
                />
              </>
            )}
          </Box>
        ) : (
          <BackDrop type="company" />
        )}
      </Grid>
    </Grid>
  );
};

export default CompanyOnboarding;
